import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths
} from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { calendarDayVariants } from '@/components/ui/button-variants';
import { IconButton } from '@/components/ui/IconButton';
import { cn } from '@/lib/cn';

type Props = {
  value?: string;
  onSelect: (isoDate: string) => void;
  min?: string;
  max?: string;
};

function toIso(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function parseIso(iso: string | undefined): Date | undefined {
  if (!iso) return undefined;
  try {
    return parseISO(iso);
  } catch {
    return undefined;
  }
}

export function Calendar({ value, onSelect, min, max }: Props) {
  const { i18n, t } = useTranslation();
  const locale = i18n.language.startsWith('ar') ? ar : enUS;
  const selected = parseIso(value);
  const minD = min ? startOfDay(parseISO(min)) : undefined;
  const maxD = max ? startOfDay(parseISO(max)) : undefined;

  const [viewMonth, setViewMonth] = useState(() => selected ?? new Date());

  const weeks = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start, end });
    const rows: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) {
      rows.push(days.slice(i, i + 7));
    }
    return rows;
  }, [viewMonth]);

  const weekdayLabels = useMemo(() => {
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) =>
      format(addDays(weekStart, i), 'EEE', { locale })
    );
  }, [locale]);

  function isDisabled(d: Date): boolean {
    const day = startOfDay(d);
    if (minD && day < minD) return true;
    if (maxD && day > maxD) return true;
    return false;
  }

  const rtl = i18n.dir() === 'rtl';
  const PrevIcon = rtl ? ChevronRight : ChevronLeft;
  const NextIcon = rtl ? ChevronLeft : ChevronRight;

  return (
    <div className="w-[280px] select-none p-2">
      <div className="mb-3 flex items-center justify-between gap-2">
        <IconButton
          size="sm"
          aria-label={t('ui.calendar.prevMonth')}
          onClick={() => setViewMonth((m) => subMonths(m, 1))}
        >
          <PrevIcon className="h-4 w-4" />
        </IconButton>
        <span className="text-sm font-semibold">
          {format(viewMonth, 'MMMM yyyy', { locale })}
        </span>
        <IconButton
          size="sm"
          aria-label={t('ui.calendar.nextMonth')}
          onClick={() => setViewMonth((m) => addMonths(m, 1))}
        >
          <NextIcon className="h-4 w-4" />
        </IconButton>
      </div>
      <div className="mb-1 grid grid-cols-7 gap-0.5 text-center text-[10px] font-medium uppercase tracking-wide text-[var(--fv-text-muted)]">
        {weekdayLabels.map((w) => (
          <span key={w}>{w}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {weeks.flat().map((day) => {
          const outside = !isSameMonth(day, viewMonth);
          const sel = selected && isSameDay(day, selected);
          const today = isToday(day);
          const off = isDisabled(day);
          return (
            <button
              key={day.toISOString()}
              type="button"
              disabled={off}
              onClick={() => onSelect(toIso(day))}
              className={cn(
                calendarDayVariants({ selected: Boolean(sel), outside }),
                today && !sel && 'ring-1 ring-[var(--fv-primary)]/40',
                off && 'cursor-not-allowed opacity-30'
              )}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex justify-center border-t border-[var(--fv-border)] pt-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            const today = new Date();
            if (!isDisabled(today)) {
              onSelect(toIso(today));
              setViewMonth(today);
            }
          }}
        >
          {t('ui.datePicker.today')}
        </Button>
      </div>
    </div>
  );
}
